import { useCallback, useMemo, useState } from 'react';

import type { SegmentExportRecipient } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { toast } from '@feathr/components';
import { useStore, useUser } from '@feathr/extender/state';
import { logError, moment, TimeFormat } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import { isDeliveryEmailNotAccountUserError } from './useExportModal.utils';
import { useTimeFrameSelect } from './useTimeFrameSelect';

export interface IDateRangeOption {
  id: string;
  name: string;
  value: [string | undefined, string | undefined];
}

export interface IExportModalValidationErrors {
  email?: string[];
  dateRange?: string[];
  exportName?: string[];
}

interface IResolvedDateRange {
  startDate: string | undefined;
  endDate: string | undefined;
}

export interface IHandleExportOptions {
  successToastAction?: IToastAction;
  successMessage?: string;
  /**
   * Replaces the generic error toast so a caller can branch on the failure —
   * e.g. surface the API's own message, or a tailored one for a specific status.
   * Sentry reporting happens either way.
   */
  onError?: (error: unknown) => void;
}

export interface IUseExportModalOptions {
  /** When provided, the hook tracks an export-name field and blocks submit while empty. */
  initialExportName?: string;
  /** Allow-list of `useTimeFrameSelect` preset IDs; omit for all. */
  presetIds?: string[];
  /** Default selected date range id. Defaults to `'all_time'`. */
  defaultDateRange?: string;
}

interface IUseExportModalReturn {
  /** The selected recipient's address; blank until one is picked. */
  email: string;
  /** The selected recipient, for the delivery select's `value`. */
  deliveryUser: SegmentExportRecipient | undefined;
  emailHadFocus: boolean;
  exportName: string;
  exportNameHadFocus: boolean;
  customDateRange: [string | undefined, string | undefined];
  dateRangeHadFocus: boolean;
  selectedDateRange: string;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  validationErrors: IExportModalValidationErrors | undefined;
  dateRangeOptions: IDateRangeOption[];
  handleChangeDeliveryUser: (recipient: SegmentExportRecipient) => void;
  handleClearDeliveryUser: () => void;
  handleChangeExportName: (newName?: string) => void;
  handleExportNameBlur: () => void;
  handleDateRangeChange: (option: IDateRangeOption) => void;
  handleCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
  handleExport: (
    exportFn: () => Promise<unknown>,
    onSuccess: () => void,
    t: (key: string, options?: Record<string, unknown>) => string,
    options?: IHandleExportOptions,
  ) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  resetFormState: () => void;
  resolveDateRange: () => IResolvedDateRange;
}

export function useExportModal(
  t: (key: string) => string,
  options: IUseExportModalOptions = {},
): IUseExportModalReturn {
  const { initialExportName, presetIds, defaultDateRange = 'all_time' } = options;
  const trackExportName = initialExportName !== undefined;
  const user = useUser();
  const { SegmentExportRecipients } = useStore();
  const userTimezone = user.get('timezone');
  const { options: timeFrameOptions } = useTimeFrameSelect();

  /*
   * Blank, not the requesting user's address: exports may only be delivered to
   * a Feathr user on the account, so the recipient is an explicit choice rather
   * than a default someone can submit without reading. See shrike#9142.
   */
  const [deliveryUser, setDeliveryUser] = useState<SegmentExportRecipient | undefined>(undefined);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [exportName, setExportName] = useState<string>(initialExportName ?? '');
  const [exportNameHadFocus, setExportNameHadFocus] = useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  const [dateRangeHadFocus, setDateRangeHadFocus] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<string>(defaultDateRange);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const filteredTimeFrameOptions = useMemo(
    () =>
      presetIds === undefined
        ? timeFrameOptions
        : timeFrameOptions.filter((opt) => presetIds.includes(opt.id)),
    [timeFrameOptions, presetIds],
  );

  const dateRangeOptions: IDateRangeOption[] = [
    { id: 'all_time', name: t('All time'), value: [undefined, undefined] },
    ...filteredTimeFrameOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      value: opt.value as [string | undefined, string | undefined],
    })),
    { id: 'custom', name: t('Custom'), value: [undefined, undefined] },
  ];

  /*
   * The recipient is the only state; the address is read off it. Holding the
   * address beside the recipient would let the two drift, so the select could
   * show one user while the request goes to another. The address is part of the
   * `GET /segment-exports/recipients` row, so this needs no extra fetch.
   */
  const email = deliveryUser?.get('email', '') ?? '';

  const handleChangeDeliveryUser = useCallback((recipient: SegmentExportRecipient): void => {
    setDeliveryUser(recipient);
    setEmailHadFocus(true);
  }, []);

  const handleClearDeliveryUser = useCallback((): void => {
    setDeliveryUser(undefined);
    setEmailHadFocus(true);
  }, []);

  const handleChangeExportName = useCallback((newName?: string): void => {
    setExportName(newName ?? '');
    setExportNameHadFocus(true);
  }, []);

  const handleExportNameBlur = useCallback((): void => {
    setExportNameHadFocus(true);
  }, []);

  const handleDateRangeChange = useCallback((option: IDateRangeOption): void => {
    setSelectedDateRange(option.id);
  }, []);

  const handleCustomDateRangeChange = useCallback(
    (value: [string | undefined, string | undefined]): void => {
      setCustomDateRange(value);
      setDateRangeHadFocus(true);
    },
    [],
  );

  const resetFormState = useCallback((): void => {
    setDeliveryUser(undefined);
    setEmailHadFocus(false);
    setExportName(initialExportName ?? '');
    setExportNameHadFocus(false);
    setCustomDateRange([undefined, undefined]);
    setDateRangeHadFocus(false);
    setSelectedDateRange(defaultDateRange);
  }, [initialExportName, defaultDateRange]);

  const resolveDateRange = useCallback((): IResolvedDateRange => {
    if (selectedDateRange === 'all_time') {
      return { startDate: undefined, endDate: undefined };
    }

    if (selectedDateRange === 'custom') {
      return { startDate: customDateRange[0], endDate: customDateRange[1] };
    }

    const selectedOption = filteredTimeFrameOptions.find((opt) => opt.id === selectedDateRange);
    if (selectedOption?.value && Array.isArray(selectedOption.value)) {
      return { startDate: selectedOption.value[0], endDate: selectedOption.value[1] };
    }

    return { startDate: undefined, endDate: undefined };
  }, [selectedDateRange, customDateRange, filteredTimeFrameOptions]);

  const validateForm = useCallback((): IExportModalValidationErrors | undefined => {
    /*
     * Presence only. The address now comes from a select over the account's own
     * users, so a malformed one is unreachable and a format check could only
     * ever fail on data the account already holds.
     */
    const emailConstraints = {
      presence: { allowEmpty: false, message: `^${t('Select a user to send the export to.')}` },
    };

    const baseErrors = validate({ email }, { email: emailConstraints }) ?? {};

    const errors: IExportModalValidationErrors = {};
    if (baseErrors.email) {
      errors.email = baseErrors.email;
    }

    if (trackExportName && exportName.trim().length === 0) {
      errors.exportName = ['Export name cannot be empty.'];
    }

    if (selectedDateRange === 'custom') {
      const startDate = customDateRange[0];
      const endDate = customDateRange[1];
      // TODO: swap to a shared getToday util once the local-vs-user-time decision lands.
      const today = (userTimezone ? moment().tz(userTimezone) : moment()).format(
        TimeFormat.isoDate,
      );

      if (!startDate || !endDate) {
        errors.dateRange = ['Both start and end dates are required for custom range.'];
      } else if (startDate > endDate) {
        errors.dateRange = ['Start date must be before end date.'];
      } else if (startDate > today || endDate > today) {
        errors.dateRange = ['Dates cannot be in the future.'];
      }
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [email, exportName, trackExportName, selectedDateRange, customDateRange, userTimezone, t]);

  const validationErrors = validateForm();

  const isSubmitDisabled = useMemo(
    () => isLoading || validationErrors !== undefined,
    [isLoading, validationErrors],
  );

  const handleExport = useCallback(
    async (
      exportFn: () => Promise<unknown>,
      onSuccess: () => void,
      t: (key: string, options?: Record<string, unknown>) => string,
      handleExportOptions: IHandleExportOptions = {},
    ): Promise<void> => {
      const { successToastAction, successMessage, onError } = handleExportOptions;
      try {
        setIsLoading(true);
        await exportFn();
        toast(successMessage ?? t('Started export. Check your email.'), {
          type: 'success',
          ...(successToastAction ? { action: successToastAction } : {}),
        });
        onSuccess();
      } catch (error) {
        logError({
          message: 'Export request failed',
          level: 'error',
          extra: { error: error instanceof Error ? error.message : String(error) },
        });
        /*
         * Checked ahead of `onError`: every surface that uses this picker gets the
         * same answer. The chosen user left the list after it loaded, so clear
         * the choice and refetch — rachis would otherwise keep serving the list
         * that still offers them.
         */
        if (isDeliveryEmailNotAccountUserError(error)) {
          toast(
            {
              title: t("This user can't receive exports anymore."),
              description: t('Select another user and try again.'),
            },
            { type: 'error' },
          );
          handleClearDeliveryUser();
          SegmentExportRecipients.list({}, { reset: true });
        } else if (onError) {
          onError(error);
        } else {
          toast(t("Couldn't export data. Try again."), { type: 'error' });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [SegmentExportRecipients, handleClearDeliveryUser],
  );

  return {
    email,
    deliveryUser,
    emailHadFocus,
    exportName,
    exportNameHadFocus,
    customDateRange,
    dateRangeHadFocus,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleChangeDeliveryUser,
    handleClearDeliveryUser,
    handleChangeExportName,
    handleExportNameBlur,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    setIsLoading,
    resetFormState,
    resolveDateRange,
  };
}
